import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { useRef, useEffect, useState, useCallback } from "react";
import * as THREE from "three";

// Use public folder path for GLB file
const gltfUrl = `${process.env.PUBLIC_URL || ""}/text/title.glb`;

function TitleModel({ onLoaded }: { onLoaded: () => void }) {
  const { scene } = useGLTF(gltfUrl);
  const meshRef = useRef<THREE.Group>(null);
  const hasCalledLoaded = useRef(false);
  const baseRotation = useRef(-Math.PI / 4);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartRotation = useRef(-Math.PI / 4);
  const dragRotation = useRef(0);
  const { gl } = useThree();

  useEffect(() => {
    if (scene && !hasCalledLoaded.current) {
      // Wait a frame to ensure everything is set up
      requestAnimationFrame(() => {
        hasCalledLoaded.current = true;
        onLoaded();
      });
    }
  }, [scene, onLoaded]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 0) { // Left mouse button
        isDragging.current = true;
        // Store the normalized X position (-1 to 1)
        const rect = gl.domElement.getBoundingClientRect();
        dragStartX.current = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        if (meshRef.current) {
          dragStartRotation.current = meshRef.current.rotation.y;
        }
        dragRotation.current = 0;
      }
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        if (meshRef.current) {
          baseRotation.current = meshRef.current.rotation.y;
        }
      }
    };

    const canvas = gl.domElement;
    canvas.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [gl]);

  // Handle rotation with click-and-drag control and auto-rotation
  useFrame((state, delta) => {
    if (meshRef.current) {
      if (isDragging.current) {
        // Calculate rotation based on drag distance
        const currentX = state.pointer.x;
        const dragDelta = currentX - dragStartX.current;
        dragRotation.current = dragStartRotation.current + dragDelta * Math.PI * 2;
        meshRef.current.rotation.y = dragRotation.current;
      } else {
        // Apply continuous auto-rotation when not dragging
        baseRotation.current += delta * 0.25;
        meshRef.current.rotation.y = baseRotation.current;
      }
    }
  });

  return (
    <group ref={meshRef} scale={[0.5, 0.5, 0.5]} position={[0, 2, 0]}>
      <primitive object={scene} />
    </group>
  );
}

// Preload the model
if (gltfUrl) {
  useGLTF.preload(gltfUrl);
}

type TooltipSetter = (next: { open: boolean; text: string; x: number; y: number }) => void;

export function Title3D({ onTooltip }: { onTooltip?: TooltipSetter }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const handleLoaded = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onTooltip) {
      onTooltip({ open: true, text: "What does your cart say about you?", x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseLeave = () => {
    if (onTooltip) {
      onTooltip({ open: false, text: "", x: 0, y: 0 });
    }
  };

  if (!gltfUrl) {
    return null;
  }

  return (
    <div
      className={`title3dContainer ${isLoaded ? "title3dLoaded" : ""}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <Canvas camera={{ position: [0, 0, 40], fov: 30 }}>
        <ambientLight intensity={1} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <TitleModel onLoaded={handleLoaded} />
      </Canvas>
    </div>
  );
}
